import React, { Fragment } from "react";
import {
  Bars3Icon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  ViewColumnsIcon,
} from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useColorMode } from "../../providers/ColorModeProvider";

interface HeaderProps {
  collapsed?: boolean;
  /** When provided, shows the desktop collapse toggle. */
  onToggleCollapse?: () => void;
  onOpenMobile: () => void;
}

// Map the first path segment to a readable breadcrumb label.
const SEGMENT_LABELS: Record<string, string> = {
  "": "Dashboard",
  documents: "Documents",
  search: "Search",
  chat: "Chat",
  analytics: "Analytics",
  users: "Users",
  settings: "Settings",
  profile: "Profile",
  admin: "Admin",
};

const Header: React.FC<HeaderProps> = ({ onToggleCollapse, onOpenMobile }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { mode, toggle } = useColorMode();
  const isAdminArea = location.pathname.startsWith("/admin");

  const segment = location.pathname.split("/")[1] ?? "";
  const crumb = SEGMENT_LABELS[segment] ?? "Dashboard";

  const initial =
    user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U";

  return (
    <header className="flex h-14 flex-shrink-0 items-center gap-3.5 border-b border-subtle bg-surface px-5">
      {/* Desktop collapse toggle */}
      {onToggleCollapse && (
        <button
          onClick={onToggleCollapse}
          title="Collapse menu"
          className="hidden h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[9px] border border-subtle text-text-muted transition-colors hover:bg-surface-2 lg:flex"
        >
          <ViewColumnsIcon className="h-[17px] w-[17px]" />
        </button>
      )}

      {/* Mobile menu button */}
      <button
        onClick={onOpenMobile}
        className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[9px] border border-subtle text-text-muted transition-colors hover:bg-surface-2 lg:hidden"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      {/* Breadcrumb */}
      <div className="hidden text-[13px] text-text-faint sm:block">
        Workspace <span className="text-text-dim">/</span>{" "}
        <span className="font-medium text-text-muted">{crumb}</span>
      </div>

      <div className="flex-1" />

      {/* Search */}
      <Link
        to="/search"
        className="hidden h-[34px] w-[230px] items-center gap-2 rounded-[9px] border border-subtle px-3 text-[13px] text-text-faint transition-colors hover:bg-surface-2 md:flex"
      >
        <MagnifyingGlassIcon className="h-[15px] w-[15px]" />
        Search documents…
      </Link>

      {/* Theme toggle */}
      <button
        onClick={toggle}
        title={mode === "dark" ? "Switch to light" : "Switch to dark"}
        className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[9px] border border-subtle text-text-muted transition-colors hover:bg-surface-2"
      >
        {mode === "dark" ? (
          <SunIcon className="h-[17px] w-[17px]" />
        ) : (
          <MoonIcon className="h-[17px] w-[17px]" />
        )}
      </button>

      {/* Avatar menu */}
      <Menu as="div" className="relative flex-shrink-0">
        <Menu.Button className="flex items-center gap-2 rounded-lg p-0.5 hover:bg-surface-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-sm font-semibold text-white">
            {initial}
          </div>
          <ChevronDownIcon className="h-4 w-4 text-text-faint" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-[color:var(--border)] rounded-lg border border-subtle bg-surface shadow-box focus:outline-none">
            <div className="px-4 py-3">
              <p className="text-sm font-medium text-text-base">
                {user?.name || "User"}
              </p>
              <p className="truncate text-sm text-text-faint">{user?.email}</p>
            </div>
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to={isAdminArea ? "/admin/profile" : "/profile"}
                    className={`block px-4 py-2 text-sm text-text-muted ${
                      active ? "bg-surface-2" : ""
                    }`}
                  >
                    Your Profile
                  </Link>
                )}
              </Menu.Item>
            </div>
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => logout()}
                    className={`block w-full px-4 py-2 text-left text-sm text-text-muted ${
                      active ? "bg-surface-2" : ""
                    }`}
                  >
                    Sign out
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </header>
  );
};

export default Header;
